/* LUX cart add-ons. No automatic additions, polling, fetch interception or app changes. */
(() => {
  'use strict';
  if (customElements.get('lux-cart-addons')) return;

  const memory = new Map();
  const sectionIds = ['cart-drawer', 'cart-icon-bubble'];
  let mutationPending = false;

  function remembered(key) {
    try {
      const value = localStorage.getItem(key);
      if (value) memory.set(key, value);
    } catch (_) { /* A blocked storage API still keeps this page's choices stable. */ }
    return memory.get(key) || '';
  }

  function remember(key, value) {
    memory.set(key, value);
    try { localStorage.setItem(key, value); } catch (_) { /* Use memory fallback. */ }
  }

  function choose(key, candidates, used) {
    const available = candidates.filter((candidate) => !used.has(candidate.dataset.productId));
    if (!available.length) return null;
    const stored = remembered(key);
    let choice = available.find((candidate) => candidate.dataset.productId === stored);
    if (!choice) {
      let random = Math.random();
      if (window.crypto && typeof window.crypto.getRandomValues === 'function') {
        random = window.crypto.getRandomValues(new Uint32Array(1))[0] / 4294967296;
      }
      choice = available[Math.floor(random * available.length)];
      remember(key, choice.dataset.productId);
    }
    used.add(choice.dataset.productId);
    return choice;
  }

  async function request(url, options = {}) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 15000);
    try {
      return await fetch(url, { ...options, credentials: 'same-origin', signal: controller.signal });
    } finally {
      clearTimeout(timer);
    }
  }

  function snapshot(sections) {
    if (!sections || typeof sections['cart-drawer'] !== 'string' ||
        typeof sections['cart-icon-bubble'] !== 'string') return null;
    const parser = new DOMParser();
    const drawerDoc = parser.parseFromString(sections['cart-drawer'], 'text/html');
    const iconDoc = parser.parseFromString(sections['cart-icon-bubble'], 'text/html');
    const drawer = drawerDoc.querySelector('cart-drawer');
    const content = drawerDoc.querySelector('#CartDrawer');
    const icon = iconDoc.querySelector('#shopify-section-cart-icon-bubble') ||
      iconDoc.querySelector('#cart-icon-bubble') || iconDoc.body;
    if (!drawer || !content || !icon || !icon.querySelector('svg')) return null;
    return { empty: drawer.classList.contains('is-empty'), content: content.innerHTML, icon: icon.innerHTML };
  }

  async function freshSnapshot(cartUrl) {
    const url = new URL(cartUrl, window.location.origin);
    url.searchParams.set('sections', sectionIds.join(','));
    const response = await request(url.href, { headers: { Accept: 'application/json' }, cache: 'no-store' });
    if (!response.ok) throw new Error('Could not refresh cart sections.');
    const result = snapshot(await response.json());
    if (!result) throw new Error('Incomplete cart sections.');
    return result;
  }

  function applySnapshot(drawer, next, ruleId) {
    const content = drawer.querySelector('#CartDrawer');
    const icon = document.getElementById('cart-icon-bubble');
    if (!content || !icon) throw new Error('Cart targets are missing.');
    const oldScroll = drawer.querySelector('cart-drawer-items');
    const scrollTop = oldScroll ? oldScroll.scrollTop : 0;

    // Keep the drawer host, its keyboard handlers and its open/closed state.
    drawer.classList.toggle('is-empty', next.empty);
    content.innerHTML = next.content;
    icon.innerHTML = next.icon;
    const overlay = drawer.querySelector('#CartDrawer-Overlay');
    if (overlay) overlay.addEventListener('click', () => drawer.close());

    const scroll = drawer.querySelector('cart-drawer-items');
    if (scroll) scroll.scrollTop = scrollTop;
    if (drawer.classList.contains('active')) {
      const inner = drawer.querySelector('.drawer__inner');
      const rule = Array.from(drawer.querySelectorAll('[data-lux-addon-rule]'))
        .find((node) => node.dataset.luxAddonRule === ruleId && !node.hidden);
      const focus = rule && rule.querySelector('[data-lux-addon-option]:not([disabled]), [data-lux-addon-variant]:not([hidden]):not([disabled]), [data-lux-addon-add]:not([disabled])');
      if (inner && typeof trapFocus === 'function') trapFocus(inner, focus || inner);
      else if (inner) (focus || inner).focus({ preventScroll: true });
    }
    document.dispatchEvent(new CustomEvent('lux:cart-updated'));
  }

  function status(drawer, message, error, cartUrl) {
    const target = drawer.querySelector('[data-lux-addon-status]') ||
      drawer.querySelector('#CartDrawer-CartErrors');
    if (!target) return;
    target.textContent = message;
    target.dataset.error = error ? 'true' : 'false';
    if (cartUrl) {
      const link = document.createElement('a');
      link.href = cartUrl;
      link.textContent = ' ' + (drawer.querySelector('lux-cart-addons')?.dataset.confirmLabel || 'Cart');
      link.className = 'link';
      target.append(link);
    }
  }

  class LuxCartAddons extends HTMLElement {
    constructor() {
      super();
      this.onChange = this.onChange.bind(this);
      this.onClick = this.onClick.bind(this);
    }

    connectedCallback() {
      this.addEventListener('change', this.onChange);
      this.addEventListener('click', this.onClick);
      // Wait for template children if this element is parsed after registration.
      queueMicrotask(() => { if (this.isConnected) this.renderChoices(); });
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('click', this.onClick);
    }

    renderChoices() {
      const used = new Set();
      const maximum = Math.max(1, Math.min(4, Number(this.dataset.maxItems) || 2));
      let visible = 0;
      for (const rule of this.querySelectorAll('[data-lux-addon-rule]')) {
        const output = rule.querySelector('[data-lux-addon-output]');
        if (!output) continue;
        output.replaceChildren();
        rule.hidden = true;
        if (visible >= maximum) continue;
        const candidates = Array.from(rule.querySelectorAll('template[data-lux-addon-product]'));
        const key = 'lux.cart-addons.v1:' + this.dataset.namespace + ':' + rule.dataset.luxAddonRule;
        const selected = choose(key, candidates, used);
        if (!selected) continue;
        output.append(selected.content.cloneNode(true));
        this.createVariantButtons(output);
        rule.hidden = false;
        visible += 1;
      }
      this.hidden = visible === 0;
      if (this.dataset.unconfirmed === 'true') {
        this.querySelectorAll('[data-lux-addon-add]').forEach((button) => { button.disabled = true; });
      }
    }

    createVariantButtons(root) {
      for (const select of root.querySelectorAll('select[data-lux-addon-variant]')) {
        if (select.dataset.luxButtons === 'true') continue;
        const options = Array.from(select.options).filter((option) => option.value && !option.disabled);
        if (!options.length) continue;
        const card = select.closest('.lux-addon-card');
        const label = Array.from(card.querySelectorAll('label')).find((node) => node.htmlFor === select.id);
        const group = document.createElement('div');
        group.className = 'lux-addon-card__options';
        group.setAttribute('role', 'group');
        group.setAttribute('aria-label', label?.textContent.trim() || select.getAttribute('aria-label') || 'Size');
        for (const option of options) {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'lux-addon-card__option';
          button.dataset.luxAddonOption = '';
          button.dataset.variantId = option.value;
          button.textContent = option.textContent;
          button.disabled = select.disabled;
          button.setAttribute('aria-pressed', String(option.value === select.value));
          group.append(button);
        }
        select.after(group);
        // Keep the existing select as the source for variant IDs and prices.
        select.hidden = true;
        select.dataset.luxButtons = 'true';
      }
    }

    onChange(event) {
      // Dawn's cart-items component treats bubbled change events as quantities.
      event.stopPropagation();
      const select = event.target.closest('[data-lux-addon-variant]');
      if (!select) return;
      const card = select.closest('.lux-addon-card');
      const button = card.querySelector('[data-lux-addon-add]');
      const option = select.options[select.selectedIndex];
      button.dataset.variantId = select.value;
      button.disabled = !select.value || mutationPending || this.dataset.unconfirmed === 'true';
      if (option && option.dataset.price) card.querySelector('[data-lux-addon-price]').textContent = option.dataset.price;
      card.querySelectorAll('[data-lux-addon-option]').forEach((choice) => {
        choice.setAttribute('aria-pressed', String(choice.dataset.variantId === select.value));
      });
    }

    onClick(event) {
      const choice = event.target.closest('[data-lux-addon-option]');
      if (choice && this.contains(choice)) {
        event.preventDefault();
        event.stopPropagation();
        const select = choice.closest('.lux-addon-card').querySelector('[data-lux-addon-variant]');
        if (choice.disabled || !select || select.disabled || mutationPending) return;
        select.value = choice.dataset.variantId;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }
      const button = event.target.closest('[data-lux-addon-add]');
      if (!button || !this.contains(button)) return;
      event.preventDefault();
      event.stopPropagation();
      if (!button.disabled) this.add(button);
    }

    async add(button) {
      const drawer = this.closest('cart-drawer');
      const variantId = button.dataset.variantId;
      if (!drawer || mutationPending || this.dataset.unconfirmed === 'true' || !/^\d+$/.test(variantId || '')) return;
      if (drawer.querySelector('.cart__items--disabled')) return;

      const labels = { ...this.dataset };
      const ruleId = button.closest('[data-lux-addon-rule]').dataset.luxAddonRule;
      const controls = Array.from(drawer.querySelectorAll(
        '.quantity__button, .quantity__input, cart-remove-button button, #CartDrawer-Checkout, [data-lux-addon-add], [data-lux-addon-variant], [data-lux-addon-option]'
      )).map((element) => ({ element, disabled: element.disabled }));
      const previousBusy = drawer.getAttribute('aria-busy');
      mutationPending = true;
      controls.forEach(({ element }) => { element.disabled = true; });
      drawer.setAttribute('aria-busy', 'true');
      button.setAttribute('aria-busy', 'true');
      status(drawer, labels.loadingLabel || 'Adding...', false);

      let confirmed = false;
      let rejectMessage = '';
      let uncertain = false;
      try {
        const response = await request(labels.addUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: variantId, quantity:1 }],
            sections: sectionIds,
            sections_url: window.location.pathname + window.location.search
          })
        });
        const data = await response.json();
        if (!response.ok) {
          rejectMessage = typeof data.description === 'string' ? data.description :
            (typeof data.message === 'string' ? data.message : labels.errorLabel);
          // Even an invalid section request can mutate the cart. Never retry POST.
          const refreshed = await freshSnapshot(labels.cartUrl);
          applySnapshot(drawer, refreshed, ruleId);
          status(drawer, rejectMessage || labels.errorLabel, true);
          return;
        }
        confirmed = true;
        const rendered = snapshot(data.sections) || await freshSnapshot(labels.cartUrl);
        applySnapshot(drawer, rendered, ruleId);
        status(drawer, labels.addedLabel || 'Added to cart', false);
      } catch (_) {
        if (confirmed) {
          // The addition succeeded, but its HTML failed. Do not add it again.
          uncertain = true;
          window.location.assign(labels.cartUrl);
        } else {
          try {
            const refreshed = await freshSnapshot(labels.cartUrl);
            applySnapshot(drawer, refreshed, ruleId);
          } catch (_) { /* Keep the cart usable and offer its authoritative page. */ }
          uncertain = !rejectMessage;
          status(drawer, rejectMessage || labels.uncertainLabel ||
            'We could not confirm the update. Check your cart before trying again.', true, labels.cartUrl);
        }
      } finally {
        mutationPending = false;
        if (previousBusy === null) drawer.removeAttribute('aria-busy');
        else drawer.setAttribute('aria-busy', previousBusy);
        button.removeAttribute('aria-busy');
        controls.forEach(({ element, disabled }) => {
          if (element.isConnected) element.disabled = disabled;
        });
        if (uncertain) {
          // An interrupted response is not proof that nothing was added.
          drawer.querySelectorAll('lux-cart-addons').forEach((element) => { element.dataset.unconfirmed = 'true'; });
          drawer.querySelectorAll('[data-lux-addon-add]').forEach((element) => { element.disabled = true; });
        }
      }
    }
  }

  customElements.define('lux-cart-addons', LuxCartAddons);

  function revealInEditor(event) {
    if (!window.Shopify || !window.Shopify.designMode || event.detail?.sectionId !== 'cart-drawer') return;
    const drawer = document.querySelector('#shopify-section-cart-drawer cart-drawer');
    if (drawer && typeof drawer.open === 'function') drawer.open();
  }
  document.addEventListener('shopify:section:select', revealInEditor);
  document.addEventListener('shopify:block:select', revealInEditor);
})();